import React from 'react';

import {
  ANTIBODY_DOMAIN_CORNER_RADIUS,
  ANTIBODY_DOMAIN_HEIGHT,
  ANTIBODY_DOMAIN_WIDTH,
  TerminusStyle,
} from 'antibodies/AntibodyConstants';
import {
  ChainDomainDisplayParams,
  ChainDomainSizingParams,
  SharedDomainParams,
} from 'antibodies/AntibodyTypedefs';

import AntibodyComponentPopover from './AntibodyComponentPopover';

export type AntibodyDomainProps = ChainDomainDisplayParams &
  ChainDomainSizingParams &
  SharedDomainParams & {
    /* What color the fill (i.e. background) of the domain should be */
    fillColor: string;
    /* What color the stroke (i.e. border) of the domain should be */
    strokeColor: string;
  };

/**
 * A colored rectangle with rounded corners and one of a small number of short-end styles.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param cornerRadius The radius of the corners
 * @param cTerminusStyle The style of the C-terminus (farther from the binding end of the antibody)
 * @param fillColor The fill color of the rectangle
 * @param height The height of the rectangle
 * @param nTerminusStyle The style of the N-terminus (closer to the binding end of the antibody)
 * @param proteinId The API ID of the protein entity associated with this domain. If passed, the domain will
 *                  be given a SmartChip-style popover with metadata about the protein.
 * @param strokeColor The stroke color of the rectangle
 * @param width The width of the rectangle
 */
export default function AntibodyDomain({
  cornerRadius = ANTIBODY_DOMAIN_CORNER_RADIUS,
  cTerminusStyle = TerminusStyle.FLAT,
  fillColor,
  height = ANTIBODY_DOMAIN_HEIGHT,
  nTerminusStyle = TerminusStyle.FLAT,
  proteinId,
  inaccessible,
  strokeColor,
  width = ANTIBODY_DOMAIN_WIDTH,
}: AntibodyDomainProps): JSX.Element {
  // Draw a rectangle with rounded corners, with the N-terminus on the top and the C-terminus on the bottom.
  // Because we can have multiple TerminusStyle on the short ends, we do this with a `path`. Draw lines or
  // arcs (or a complex shape comprised of multiple lines and/or arcs) between eight points: the endpoints of
  // each side of the rectangle, which are knocked back slightly in order to create the rounded corners.
  const nTerminusLeft = {x: cornerRadius, y: 0};
  const nTerminusRight = {x: width - cornerRadius, y: 0};
  const rightTop = {x: width, y: cornerRadius};
  const rightBottom = {x: width, y: height - cornerRadius};
  const cTerminusRight = {x: width - cornerRadius, y: height};
  const cTerminusLeft = {x: cornerRadius, y: height};
  const leftBottom = {x: 0, y: height - cornerRadius};
  const leftTop = {x: 0, y: cornerRadius};
  // Now draw lines between them. Start at the top left and draw clockwise.
  const start = `M ${nTerminusLeft.x} ${nTerminusLeft.y}`;
  let nTerminusD: string;
  if (nTerminusStyle === TerminusStyle.FLAT) {
    nTerminusD = `L ${nTerminusRight.x} ${nTerminusRight.y}`;
  } else {
    nTerminusD = `L ${width / 2} ${height / 8} L ${nTerminusRight.x} ${nTerminusRight.y}`;
  }
  const nToRightArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${rightTop.x} ${rightTop.y}`;
  const rightD = `L ${rightBottom.x} ${rightBottom.y}`;
  const rightToCArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${cTerminusRight.x} ${cTerminusRight.y}`;
  let cTerminusD: string;
  if (cTerminusStyle === TerminusStyle.FLAT) {
    cTerminusD = `L ${cTerminusLeft.x} ${cTerminusLeft.y}`;
  } else {
    cTerminusD = `L ${width / 2} ${(height * 7) / 8} L ${cTerminusLeft.x} ${cTerminusLeft.y}`;
  }
  const cToLeftArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${leftBottom.x} ${leftBottom.y}`;
  const leftD = `L ${leftTop.x} ${leftTop.y}`;
  const leftToNArc = `A ${cornerRadius} ${cornerRadius} 0 0 1 ${nTerminusLeft.x} ${nTerminusLeft.y}`;
  const paths = [
    start,
    nTerminusD,
    nToRightArc,
    rightD,
    rightToCArc,
    cTerminusD,
    cToLeftArc,
    leftD,
    leftToNArc,
  ].join(' ');
  const pathD = `${paths} Z`;
  return (
    <AntibodyComponentPopover proteinId={proteinId} inaccessible={inaccessible}>
      <path
        d={pathD}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeColor === 'transparent' ? 0 : width / 16}
        shapeRendering="geometricPrecision"
      />
    </AntibodyComponentPopover>
  );
}
